import { DotLottie, DotLottieReact } from '@lottiefiles/dotlottie-react'
import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useEffect, useRef, useState } from 'react'
import { useDialogs } from '../../../sharedComponents/src/Contexts/DialogsContext'

interface StreakIndicatorProps {
    currentStreak: number
    bestStreak?: number
    active?: boolean
    variant?: 'practice' | 'daily'
    /**
     * Signal that indicates initial data has loaded.
     * Pass a changing value like puzzleId to mark when data is ready.
     * Animations won't trigger until this value changes at least once.
     */
    dataReadySignal?: string | number
}

const StreakIndicator: React.FC<StreakIndicatorProps> = ({
    currentStreak,
    bestStreak,
    active = true,
    variant = 'practice',
    dataReadySignal,
}) => {
    const theme = useTheme()
    const { setPauseDialogs } = useDialogs()
    const [streakAnimated, setStreakAnimated] = useState(false)
    const [fullAnimated, setFullAnimated] = useState(false)
    const [isDataReady, setIsDataReady] = useState(false)
    const prevStreakRef = useRef(currentStreak)
    const prevBestRef = useRef(bestStreak)
    const prevDataSignalRef = useRef(dataReadySignal)
    const mainLottieRef = useRef<DotLottie | null>(null)
    const burstLottieRef = useRef<DotLottie | null>(null)

    useEffect(() => {
        return () => {
            // Cleanup: ensure dialogs are unpaused when component unmounts
            setPauseDialogs(false)
        }
    }, [])

    // Track when initial data has loaded
    useEffect(() => {
        if (!isDataReady && dataReadySignal !== undefined && dataReadySignal !== prevDataSignalRef.current) {
            setIsDataReady(true)
        }
        prevDataSignalRef.current = dataReadySignal
    }, [dataReadySignal, isDataReady])

    // Detect when currentStreak changes
    useEffect(() => {
        // Don't animate until data is ready (if dataReadySignal is being used)
        if (dataReadySignal !== undefined && !isDataReady) {
            prevStreakRef.current = currentStreak
            prevBestRef.current = bestStreak
            return
        }

        // Animate if value changed and current is > 0
        if (prevStreakRef.current !== currentStreak && currentStreak > 0 && prevStreakRef.current !== undefined) {
            // Only animate if going from 0→1 OR from any positive number to another
            const isFirstStreak = prevStreakRef.current === 0 && currentStreak === 1
            const isSubsequentStreak = prevStreakRef.current > 0

            if (isFirstStreak || isSubsequentStreak) {
                setStreakAnimated(true)
                setPauseDialogs(true)
                if (isFirstStreak || variant === 'daily') {
                    setFullAnimated(true)
                }
            }
        }
        prevStreakRef.current = currentStreak
    }, [currentStreak, bestStreak, dataReadySignal, isDataReady])

    // Detect when bestStreak changes
    useEffect(() => {
        // Don't animate until data is ready (if dataReadySignal is being used)
        if (dataReadySignal !== undefined && !isDataReady) {
            prevBestRef.current = bestStreak
            return
        }

        // Animate if value changed and current is > 0
        if (prevBestRef.current !== bestStreak && bestStreak && bestStreak > 0 && prevBestRef.current !== undefined) {
            const isFirstBest = (prevBestRef.current === 0 || prevBestRef.current === undefined) && bestStreak === 1
            const isNewBest = prevBestRef.current > 0

            if (isFirstBest || isNewBest) {
                setPauseDialogs(true)
                setFullAnimated(true)
            }
        }
        prevBestRef.current = bestStreak
    }, [bestStreak, dataReadySignal, isDataReady])

    const flameColor = active ? '#FF5F00' : '#393939'
    const strokeColor = active ? '#FF3600' : '#777777'
    const tagStroke = fullAnimated && streakAnimated ? '#FF5F00' : '#777777'
    const cardBg = '#101820'

    return (
        <Box
            sx={{
                position: 'relative',
                height: '48px',
                width: variant === 'practice' ? '167px' : '108px',
                display: 'flex',
                alignItems: 'center',
                zIndex: 1,
            }}
        >
            {/* Burst Animation - Behind everything */}
            {fullAnimated && (
                <Box
                    sx={{
                        position: 'absolute',
                        right: '0',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '120%',
                        zIndex: 0,
                        pointerEvents: 'none',
                    }}
                >
                    <DotLottieReact
                        speed={0.75}
                        src="/assets/animations/puzzles/streaks/StreakFlame_Burst.lottie"
                        loop={false}
                        autoplay={true}
                        layout={{ fit: 'fit-height', align: [0, 0] }}
                    />
                </Box>
            )}

            {/* Flame Icon SVG */}
            <Box
                component="svg"
                sx={{
                    position: 'absolute',
                    left: '1px',
                    top: '1.5px',
                    width: '36px',
                    height: '44.3px',
                    zIndex: 3,
                    // opacity: streakAnimated ? 0 : 1,
                    opacity: 1,
                    transition: 'opacity 0.3s ease',
                }}
                viewBox="0 0 38 48"
                fill="none"
            >
                <path
                    d="M22 1.5C22 8.5 24.13 14.5 27.5 14.5C28.99 14.5 30.49 14.23 31 11.5C36.42 17.34 37.49 25.7838 36.69 30.3038V30.3438C35.49 39.0738 28 45.8038 18.93 45.8038C9.02 45.8038 1 37.8938 1 27.8838C1 23.9538 2.77 18.7438 3.67 16.6938C6.21 21.0238 10.31 20.66 11 15.5C11.4939 11.8062 14.5 3 22 1.5Z"
                    fill={flameColor}
                    stroke={strokeColor}
                    strokeWidth="2"
                />
            </Box>

            {/* Main Flame Animation - Overlays the static flame when streak changes */}
            {streakAnimated && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: '1px',
                        top: '1.5px',
                        width: '36px',
                        height: '44.3px',
                        zIndex: 4,
                        pointerEvents: 'none',
                        opacity: 1,
                        overflow: 'visible',
                    }}
                >
                    <DotLottieReact
                        dotLottieRefCallback={(ref) => {
                            mainLottieRef.current = ref
                            // Listen for animation complete event
                            if (ref) {
                                ref.addEventListener('complete', () => {
                                    setStreakAnimated(false)
                                    setFullAnimated(false)
                                    setTimeout(() => {
                                        setPauseDialogs(false)
                                    }, 333)
                                })
                            }
                        }}
                        src="/assets/animations/puzzles/streaks/StreakFlame_Main.lottie"
                        loop={false}
                        autoplay={true}
                        style={{
                            width: '100px',
                            height: '100px',
                            transform: 'translate(-32px, -28px)',
                        }}
                    />
                </Box>
            )}

            {/* Number on Flame */}
            <Typography
                variant="h6"
                sx={{
                    position: 'absolute',
                    left: '19.5px',
                    top: '28.84px',
                    transform: 'translate(-50%, -50%)',
                    fontFamily: 'Fira Sans',
                    fontSize: '18px',
                    fontWeight: 700,
                    color: 'white',
                    textTransform: 'uppercase',
                    lineHeight: '24px',
                    textShadow: '0px 2px 0px rgba(0, 0, 0, 0.4)',
                    zIndex: 5,
                }}
            >
                {currentStreak}
            </Typography>

            {/* Card with Text */}
            <Box
                sx={{
                    position: 'absolute',
                    left: '16px',
                    top: variant === 'practice' ? '17.34px' : '17.84px',
                    backgroundColor: cardBg,
                    border: `${fullAnimated && streakAnimated ? 2 : 1}px solid ${tagStroke} !important`,
                    borderRadius: '4px',
                    paddingLeft: '24px',
                    paddingRight: '4px',
                    paddingTop: variant === 'practice' ? 0 : '4px',
                    paddingBottom: variant === 'practice' ? 0 : '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '23px',
                    minWidth: variant === 'practice' ? '143px' : '92px',
                    zIndex: 2,
                    overflow: 'visible',
                    transform: fullAnimated && streakAnimated ? 'scale(1.2)' : 'scale(1)',
                    transformOrigin: 'left center',
                    transition: 'transform 0.45s ease',
                }}
            >
                {variant === 'practice' ? (
                    <Typography
                        sx={{
                            fontFamily: 'Fira Sans',
                            fontSize: '16px',
                            color: 'white',
                            textTransform: 'uppercase',
                            lineHeight: '24px',
                            whiteSpace: 'nowrap',
                            position: 'relative',
                        }}
                    >
                        <Box component="span" sx={{ fontWeight: 700 }}>
                            Streak |{' '}
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 400,
                                color: bestStreak && currentStreak === bestStreak ? '#FF5F00' : 'white',
                            }}
                        >
                            Best {bestStreak}
                        </Box>
                    </Typography>
                ) : (
                    <Typography
                        sx={{
                            fontFamily: 'Fira Sans',
                            fontSize: '18px',
                            fontWeight: 700,
                            color: 'white',
                            textTransform: 'uppercase',
                            lineHeight: '24px',
                            whiteSpace: 'pre-wrap',
                            position: 'relative',
                            pr: '3px',
                        }}
                    >
                        Streak
                    </Typography>
                )}
            </Box>
        </Box>
    )
}

export default StreakIndicator
